<template>
	<div class="modal-card" style="width: auto; min-width: 680px">
		<header class="modal-card-head">
			<p class="modal-card-title">{{ addEditText }}</p>
		</header>
		<section class="modal-card-body">
			<div class="columns is-multiline">
				<div class="column is-4">
					<o-field label="Title*">
						<o-input v-model="row.Title"></o-input>
					</o-field>
					<o-field label="Breadcrumb title">
						<o-input v-model="row.BreadcrumbTitle"></o-input>
						<template v-slot:message>
							The title as it appears in the breadcrumb navigation. Is assumed to be the same as
							Title if left blank.
						</template>
					</o-field>
					<o-field label="Path name*">
						<o-input
							v-model="row.VueRouterPath"
							@blur="row.VueRouterPath = row.VueRouterPath.replace(/\s+/g, '-')"></o-input>
						<template v-slot:message>
							This is the name that appears in the page URL (E.g. /metrics/
							<strong>reports</strong>
							). Must be unique among its siblings. (There cannot be two pages named "reports" under
							/metrics.)
							<p v-if="routePathTaken" style="color: indianred">
								This path has already been taken.
							</p>
						</template>
					</o-field>
					<o-field label="Internal name*">
						<template v-slot:default>
							<o-input v-model="row.VueRouterName" :disabled="!!uuid"></o-input>
						</template>
						<template v-slot:message>
							This is a distinct name that represents the page, similar to the path name. Must be
							unique among all pages.
							<p v-if="routeNameTaken" style="color: indianred">
								This name has already been taken.
							</p>
						</template>
					</o-field>

					<!-- <o-field label="Redirect">
						<template v-slot:default>
							<o-input v-model="row.RedirectLink"></o-input>
						</template>
						<template v-slot:message>Automatically redirects when navigating to this url.</template>
					</o-field> -->
				</div>
				<div class="column is-4">
					<o-field label="Redirect">
						<template v-slot:default>
							<o-input v-model="row.RedirectLink"></o-input>
						</template>
						<template v-slot:message>
							Internal name of page that users get redirected to when visiting this page. Leave
							blank to not redirect.
						</template>
					</o-field>
					<o-field label="Publish date">
						<o-datepicker
							v-model="publishDate"
							icon="calendar"
							:min-date="minDate"
							:max-date="maxDate"></o-datepicker>
						<template v-slot:message>
							The date this page becomes visible. Page is visible if left blank.
							<em>WIP</em>
						</template>
					</o-field>
					<o-field label="Expiration date">
						<o-datepicker
							v-model="expirationDate"
							icon="calendar"
							:min-date="minDate"
							:max-date="maxDate"></o-datepicker>
						<template v-slot:message>
							Date after which this page will not be visible. Page does not expire if left blank.
							<em>WIP</em>
						</template>
					</o-field>
					<o-field label="Exclude from nav">
						<o-switch v-model="row.ExcludeFromNav" :trueValue="true" :falseValue="false"></o-switch>
					</o-field>
					<o-field label="Do not open">
						<o-switch v-model="row.DoNotOpen" :trueValue="true" :falseValue="false"></o-switch>
						<template v-slot:message>
							If checked, the corresponding navbar item will not open the page when clicked. This is
							useful for grouping pages when a parent page isn't needed.
						</template>
					</o-field>
					<o-field label="Hide Drawer">
						<o-switch v-model="row.HideDrawer" :trueValue="true" :falseValue="false"></o-switch>
					</o-field>
					<!-- <o-field label="Do not link">
						<o-checkbox v-model="row.DoNotLink" :trueValue="true" :falseValue="false"></o-checkbox>
					</o-field> -->
				</div>
				<div class="column is-4">
					<o-field label="Meta Title">
						<o-input type="text" v-model="row.MetaTitle"></o-input>
					</o-field>
					<o-field label="Meta Description">
						<o-input type="textarea" v-model="row.MetaDescription"></o-input>
					</o-field>

					<div>
						<img v-lazy="metaImage" v-if="!reloadMetaImage" />
						<explore-btn @input="SelectImage">Choose an image</explore-btn>
					</div>

					<div>
						<o-field label="Download box file">
							<o-switch
								v-model="row.HasFile"
								:trueValue="true"
								:falseValue="false"
								@change="
									(b) => {
										if (row.HasFile == false) {
											row.File = null;
										}
									}
								"></o-switch>
						</o-field>
						<o-field label="Download button text">
							<o-input v-model="row.DownloadBtn" placeholder="Download file"></o-input>
						</o-field>
						<div v-if="row.HasFile == true">
							<span v-if="row.File">
								<o-icon icon="paperclip"></o-icon>
								{{ row.File.name }}
							</span>
							<span v-else>No file attached.</span>
							<div>
								<o-button size="small" @click="OpenBoxFiles">Attach a file</o-button>
							</div>
						</div>
					</div>
				</div>
				<div class="column is-12">
					<p>
						Global filters are applied across all sections for the page. If none are set, the parent
						filters will be used.
					</p>
					<o-field label="Content filter">
						<content-filter v-model="contentFilter"></content-filter>
					</o-field>
					<o-field label="User filter">
						<search-filter v-model="usersFilter" type="users"></search-filter>
					</o-field>
					<o-field label="Assignment filter">
						<search-filter v-model="assignmentsFilter" type="assignments"></search-filter>
					</o-field>
					<o-field label="Company filter">
						<search-filter v-model="companiesFilter" type="companies"></search-filter>
					</o-field>
				</div>
			</div>
		</section>
		<footer class="modal-card-foot is-justify-content-space-between">
			<div>
				<o-button :disabled="saving || !isValid" variant="success" icon-left="save" @click="Save">
					Save
				</o-button>
				<o-button variant="light" @click="Back">Cancel</o-button>
			</div>
			<div>
				<router-link
					:to="{ name: metadata.VueRouterName }"
					target="_blank"
					class=""
					v-if="!isNew && metadata.VueRouterName && !isSamePage">
					<o-button variant="primary" icon-left="external-link">View</o-button>
				</router-link>
			</div>
		</footer>
	</div>
</template>
<script>
	import { ref, computed, inject, onMounted, watch, onBeforeMount, nextTick } from "vue";
	import { useRouter } from "vue-router";
	import exploreBtn from "@/components/webComponents/button/explorer.vue";
	import boxFilesModal from "@/components/modals/boxFilesModal.vue";
	import { stringUtil } from "@/services/utils";
	import searchFilter from "@/components/webComponents/filter/searchFilter.vue";
	import contentFilter from "@/components/webComponents/filter/contentFilter.vue";
	import { boxFiles } from "../widgets";
	export default {
		props: {
			uuid: {
				type: [String, Number],
				default: undefined
			},
			metadata: {
				type: Object,
				default: () => {
					return {};
				}
			},
			siblings: {
				type: Array,
				default: () => {
					return [];
				}
			},
			flatroutes: {
				type: [Array],
				default: () => {
					return [];
				}
			},
			isNew: {
				type: Boolean,
				default: false
			}
		},
		components: {
			exploreBtn,
			searchFilter,
			contentFilter
		},
		setup(props, context) {
			const $http = inject("$http");
			const moment = inject("$moment");
			const oruga = inject("$oruga");
			const router = useRouter();

			const routeNameTaken = ref();
			const routePathTaken = ref();
			const saving = ref(false);
			const row = ref(props.metadata);
			//console.log(row.value);
			const publishDate = ref(null);
			const expirationDate = ref(null);
			const contentFilter = ref();
			const usersFilter = ref();
			const assignmentsFilter = ref();
			const companiesFilter = ref();
			const reloadMetaImage = ref(false);
			const addEditText = computed(() => {
				return props.uuid ? row.value.Title : "Add Page";
			});

			const minDate = ref(moment("2008-01-01").toDate());
			const maxDate = ref(moment().toDate());

			const isValid = computed(() => {
				return !!(row.value.Title && row.value.VueRouterName && row.value.VueRouterPath);
			});

			const isSamePage = computed(() => {
				return router.currentRoute.value.name === props.metadata.VueRouterName;
			});

			const Back = () => {
				context.emit("close");
			};

			const TriggerError = (errorType) => {
				if (errorType == "nameTaken") {
					routeNameTaken.value = true;
				}
			};

			const Save = async () => {
				saving.value = true;
				if (await CheckName(row.value.VueRouterName, row.value.VueRouterPath)) {
					BindDates();
					StringifyProps();

					context.emit("save", {
						uuid: props.uuid,
						metadata: row.value,
						close: Back,
						error: TriggerError,
						saving: saving
					});
				}
				saving.value = false;
			};

			const CheckName = async (name, path) => {
				const uuid = row.value.UUID < 0 ? null : row.value.UUID;
				if (!LocalCheckInternalName(name, uuid)) {
					routeNameTaken.value = true;
				}
				if (!LocalCheckPathName(path)) {
					routePathTaken.value = true;
				}
				if (routeNameTaken.value || routePathTaken.value) {
					return false;
				}
				const check = await $http
					.post("/api/v2/routes/check", { uuid: uuid, name: name })
					.then((resp) => resp.data)
					.catch((err) => false);
				return check;
			};

			const SetupDates = () => {
				if (props.metadata.PublishDate && props.metadata.PublishDate != "0000-00-00") {
					const segments = props.metadata.PublishDate.split("-");
					publishDate.value = new Date(segments[0], parseInt(segments[1]) - 1, segments[2]);
				}

				if (props.metadata.ExpirationDate && props.metadata.ExpirationDate != "0000-00-00") {
					const segments = props.metadata.ExpirationDate.split("-");
					expirationDate.value = new Date(segments[0], parseInt(segments[1]) - 1, segments[2]);
				}
			};

			const ParseProps = () => {
				if (props.metadata.ContentFilterJSON) {
					try {
						contentFilter.value = JSON.parse(props.metadata.ContentFilterJSON);
					} catch (err) {
						console.error(err);
					}
				}
				if (props.metadata.UsersFilterJSON) {
					try {
						usersFilter.value = JSON.parse(props.metadata.UsersFilterJSON);
					} catch (err) {
						console.error(err);
					}
				}
				if (props.metadata.AssignmentsFilterJSON) {
					try {
						assignmentsFilter.value = JSON.parse(props.metadata.AssignmentsFilterJSON);
					} catch (err) {
						console.error(err);
					}
				}
				if (props.metadata.CompaniesFilterJSON) {
					try {
						companiesFilter.value = JSON.parse(props.metadata.CompaniesFilterJSON);
					} catch (err) {
						console.error(err);
					}
				}
				if (props.metadata.FileJSON) {
					try {
						row.value.File = JSON.parse(props.metadata.FileJSON);
					} catch (err) {
						console.error(err);
					}
				}
			};

			const metaImage = computed(() => {
				if (row.value.MetaImage)
					return (
						process.env.VUE_APP_API +
						"api/v2/cms/metaImage/" +
						row.value.MetaImage.OID +
						"?type=" +
						row.value.MetaImage.Type.Identifier
					);
				else {
					return process.env.VUE_APP_API + "api/v2/page/" + props.metadata.UUID + "/metaImage";
				}
			});

			const SelectImage = async (result) => {
				row.value.MetaImage = result;
				reloadMetaImage.value = true;
			};

			const OpenBoxFiles = () => {
				oruga.modal.open({
					props: {
						select: true
					},
					component: boxFilesModal,
					events: {
						select(file) {
							row.value.File = file;
						}
					}
				});
			};

			const BindDates = () => {
				row.value.PublishDate = moment(publishDate.value).format("YYYY-MM-DD");
				row.value.ExpirationDate = moment(expirationDate.value).format("YYYY-MM-DD");
			};

			const StringifyProps = () => {
				row.value.ContentFilterJSON = JSON.stringify(contentFilter.value);
				row.value.UsersFilterJSON = JSON.stringify(usersFilter.value);
				row.value.AssignmentsFilterJSON = JSON.stringify(assignmentsFilter.value);
				row.value.CompaniesFilterJSON = JSON.stringify(companiesFilter.value);
				row.value.FileJSON = JSON.stringify(row.value.File);
			};

			const LocalCheckPathName = (name) => {
				if (!props.siblings || props.siblings.length === 0) return true;
				return !props.siblings.some((x) => x.VueRouterPath === name);
			};
			const SuggestedPathName = () => {
				let suggestedName = stringUtil.toKebabCase(row.value.Title);
				if (props.siblings && props.siblings.length > 0) {
					let counter = 0;
					while (props.siblings.some((x) => x.VueRouterPath === suggestedName)) {
						suggestedName = row.value.Title + "_" + counter;
						++counter;
					}
				}
				return suggestedName;
			};

			const LocalCheckInternalName = (name, uuid) => {
				let pool = props.flatroutes;
				if (uuid) {
					pool = pool.filter((x) => x.UUID !== uuid);
				}
				return !pool.some((x) => x.VueRouterName === name);
			};
			const SuggestedInternalName = () => {
				let suggestedName = stringUtil.toKebabCase(row.value.Title);

				let counter = 0;
				while (!LocalCheckInternalName(suggestedName, row.value.UUID)) {
					suggestedName = row.value.Title + "_" + counter;
					++counter;
				}
				return suggestedName;
			};

			watch(
				() => row.value.Title,
				(n) => {
					routeNameTaken.value = false;
					routePathTaken.value = false;
					if (!props.uuid) {
						row.value.VueRouterName = SuggestedInternalName();
					}
					row.value.VueRouterPath = SuggestedPathName();
					row.value.MetaTitle = n;
				}
			);

			watch(
				() => reloadMetaImage.value,
				(n) => {
					nextTick(() => {
						reloadMetaImage.value = false;
					});
				}
			);

			// onBeforeMount(() => {
			// 	row.value.OGNode = row.value.OGFieldOverrides;
			// });

			onMounted(() => {
				SetupDates();
				ParseProps();
			});

			return {
				minDate,
				maxDate,
				reloadMetaImage,
				routeNameTaken,
				routePathTaken,
				saving,
				row,
				publishDate,
				expirationDate,
				addEditText,
				contentFilter,
				usersFilter,
				assignmentsFilter,
				companiesFilter,
				isValid,
				isSamePage,
				metaImage,
				Back,
				Save,
				SelectImage,
				OpenBoxFiles
			};
		}
	};
</script>
